import React, {useEffect, useRef, useState} from "react";
import {Box, Breadcrumbs, Container, Grid, Link, MenuItem, Paper, Select, Stack, Typography} from "@mui/material";
import TimesheetSetting from "@/pages/att/Timesheet/component/TimsheetSetting";
import {object, TypeOf, z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import PatternSetting, {PatternSettingFields} from "@/pages/att/Timesheet/component/PatternSetting";
import BreakTimeSetting, {BreakTimeSettingFields} from "@/pages/att/Timesheet/component/BreakTimeSetting";
import TimesheetService from "@/services/att/services/TimesheetService";
import {useLocation, useNavigate} from "react-router-dom"
import {useSnackbar} from "notistack";
import GenericService from "@/services/GenericService";
import {ATTEndpoints} from "@/services/att/endpoints";
import {useTranslation} from "react-i18next";
import {CancelButton, SaveButton} from "@/components/data-grid/CustomButton";
import NGTextField from "@/components/ngteco-form/NGTextField";
import NGInputLabel from "@/components/ngteco-form/NGInputLabel";
import Switch from '@mui/material/Switch';
import Util from "@/utils/Util";
import Dialog from "@/components/dialog";
import {VideoPlayer7} from "@/parts/Header/components/HelpInfo";

/**
 * Validators */

export type TimeSheetProps = {
  info?: any;
  refresh?: any;
}

const NewTimesheetQuick: React.FC<TimeSheetProps> = (props) => {
  const {t} = useTranslation();
  const timesheetSchema = object({
    name: z.string().min(1, t("common.This field is required!"))
      .max(30, t("common.The length of field cannot exceed digits", {number: 30})),
    description: z.string().max(200, t("common.The length of field cannot exceed digits", {number: 200})),
    timesheet_color: z.string()
      .max(30, t("common.The length of field cannot exceed digits", {number: 30})).optional(),
  })
  type TimesheetInput = TypeOf<typeof timesheetSchema>;
  const location = useLocation()
  const state = location.state as { id: string }
  const {enqueueSnackbar} = useSnackbar();
  const patternRef = useRef<{ pattern: any }>();
  const breakTimeRef = useRef<{ breakTime: any }>();
  const sheetRef = useRef<{ sheet: any }>();
  const title = state?.id ? t("common.View TimeSheet") : t("common.Add TimeSheet");
  const [timesheet] = useState<any[]>([]);
  const [pattern] = useState<PatternSettingFields>({
    day_change: "00:00",
    punch_interval: 1,
    max_hrs: 10,
    pairing: 'first_last',
    overtime_remind: 0,
    required_record: "any",
  });
  const [breakTime] = useState<BreakTimeSettingFields>({
    break_times_flag: 100, break_switch: false,
    break_start_1: new Date('1990-01-01T12:00:00'), break_end_1: new Date('1990-01-01T12:30:00'),
    pairing_rule_1: "auto_deducted",
    break_start_2: new Date('1990-01-01T13:00:00'), break_end_2: new Date('1990-01-01T13:30:00'),
    pairing_rule_2: "auto_deducted",
    break_start_3: new Date('1990-01-01T14:00:00'), break_end_3: new Date('1990-01-01T14:30:00'),
    pairing_rule_3: "auto_deducted",
  });
  const [periodCycle] = useState<number>(1);
  const [break_loading, setBreakLoading] = React.useState(true);
  const [overtime_loading, setOvertimeLoading] = React.useState(false);
  const [option, setOption] = React.useState('clocking');

  function handleClick() {
    setBreakLoading(true);
  }

  const {
    register,
    trigger,
    setValue,
    handleSubmit,
    watch,
    formState: {errors},
    getValues
  } = useForm<TimesheetInput>({
    resolver: zodResolver(timesheetSchema),
  });

  const selectedColor = watch("timesheet_color");
  const presetColors = [
    '#1E88E5', '#43A047', '#F4511E', '#8E24AA',
    '#FBC02D', '#00ACC1', '#E53935', '#5E35B1',
    '#FB8C00', '#3949AB', '#6D4C41', '#00796B',
    '#C0CA33', '#546E7A'
  ];
  useEffect(() => {
    if (state?.id) {
      GenericService.retrieve(ATTEndpoints.ATT_TIMESHEET_URI, `${state.id}`).then((res) => {
        setValue("name", res.data?.name);
        setValue("description", res.data?.description);
        setValue('timesheet_color', res.data?.reserve?.timesheet_color)
        setOvertimeLoading(res.data?.pattern.overtime_flag);
        setOption(res.data?.pattern.option)
        if (res.data?.pattern.break_pairs.length === 0) {
          setBreakLoading(true);
        }
      })
    }
  }, []);
  const scrollTo = (name: string) => {
    const anchorElement = document.getElementsByName(name)[0]
    if (anchorElement) {
      anchorElement.scrollIntoView({behavior: "smooth"})
    }
  }
  const onSubmit = async (formData: any) => {
    let result;
    if (!formData.timesheet_color) {
      formData.timesheet_color = '#1E88E5'
    }
    const sheetData = await sheetRef.current?.sheet();
    const patternData = await patternRef.current?.pattern();
    const punch_interval = patternData?.punch_interval
    if (patternData.option === 'clocking') {
      let max_punch_interval = 999;
      sheetData?.sheet.map((sheet_obj: any) => {
        const minuteDiff = Util.timeFn(Util.time2datetime(sheet_obj.check_in_start),
          Util.time2datetime(sheet_obj.check_out_end))
        if (minuteDiff < max_punch_interval) {
          max_punch_interval = minuteDiff
        }
      })
      if (punch_interval > max_punch_interval) {
        enqueueSnackbar(t("att.timeSheet.The Punch Interval cannot exceed the check-out and check-in interval"), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
        return
      }
    }
    const breakTimeData = await breakTimeRef.current?.breakTime();
    // patternData.check_in = patternData.check_in.toString().slice(16, 21)
    // patternData.check_out = patternData.check_out.toString().slice(16, 21)
    // patternData.day_change = patternData.day_change.toString().slice(16, 21)
    const break_pairs = []
    if (break_loading) {
      breakTimeData.break_start_1 = breakTimeData.break_start_1.toString().slice(16, 21)
      breakTimeData.break_end_1 = breakTimeData.break_end_1.toString().slice(16, 21)
      breakTimeData.break_start_2 = breakTimeData.break_start_2.toString().slice(16, 21)
      breakTimeData.break_end_2 = breakTimeData.break_end_2.toString().slice(16, 21)
      breakTimeData.break_start_3 = breakTimeData.break_start_3.toString().slice(16, 21)
      breakTimeData.break_end_3 = breakTimeData.break_end_3.toString().slice(16, 21)
      breakTimeData.break_switch = break_loading
      break_pairs.push({
        "start": breakTimeData.break_start_1,
        "end": breakTimeData.break_end_1,
        "pairing_rule": breakTimeData.pairing_rule_1
      })
      if (breakTimeData.break_times_flag.toString().charAt(1) === "1") {
        break_pairs.push({
          "start": breakTimeData.break_start_2,
          "end": breakTimeData.break_end_2,
          "pairing_rule": breakTimeData.pairing_rule_2
        })
      }
      if (breakTimeData.break_times_flag.toString().charAt(2) === "1") {
        break_pairs.push({
          "start": breakTimeData.break_start_3,
          "end": breakTimeData.break_end_3,
          "pairing_rule": breakTimeData.pairing_rule_3
        })
      }
    }
    patternData["break_pairs"] = break_pairs
    patternData["max_hrs"] = parseInt(patternData.max_hrs)
    patternData["overtime_flag"] = overtime_loading
    const mix_patternData = {...sheetData, ...patternData}
    delete mix_patternData.sheet
    if (sheetData && patternData) {
      const data = {
        ...formData,
        ...sheetData,
        pattern: mix_patternData
      };
      if (state?.id) {
        result = await TimesheetService.updateTimesheet(state.id, data);
      } else if (sheetData.cycle < 1 || sheetData.cycle > 12 || isNaN(sheetData.cycle) ||
        String(sheetData.cycle).indexOf(".") > -1) {
        scrollTo("cycle")
      } else {
        result = await TimesheetService.addTimesheet(data);
        if (result.status < 400) {
          enqueueSnackbar(t("common.Time sheet created successfully."), {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
        }
      }
    }
  };
  const KnowAboutThis = () => {
    return (
      <Grid sx={{height: 'calc(100% - 60px)', backgroundColor: '#ffffff'}}>
        <Grid container={true} rowSpacing={3} component={"form"}>
          <Grid item sx={{display: "inline-flex", flexDirection: "column", marginLeft: -1,
            textAlign: "left"}} rowSpacing={3}>
            <Typography sx={{'p': '5px 0'}}>{t("att.timeSheet.Know about tip1")}</Typography>
            <Typography sx={{'p': '5px 0'}}>{t("att.timeSheet.Know about tip2")}</Typography>
            <Typography sx={{'p': '5px 0'}}>{t("att.timeSheet.Know about tip3")}</Typography>
            <Typography sx={{ marginTop: "-10px", color: "#999999"}}>{t("att.timeSheet.Know about tipExample")}</Typography>
            <Typography sx={{'p': '5px 0'}}>{t("att.timeSheet.Know about tip4")}</Typography>
            {/* <Typography sx={{'p': '10px 0'}}>{t("att.timeSheet.Know about tip5")}</Typography> */}
          </Grid>
        </Grid>
      </Grid>
    );
  };
  return (
    <Box>
      <Grid container={true} rowSpacing={1} sx={{margin: "0"}}>
        <Grid item xs={10} sx={{p: "0 16px"}}>
          <Grid container={true} rowSpacing={2} columnSpacing={5} component={"form"}
                onSubmit={handleSubmit(onSubmit)}>
            <Grid item={true} xs={4}>
              <NGTextField
                fullWidth={true}
                placeholder={t("common.Timesheet Name")}
                margin={"normal"}
                label={t("common.Timesheet Name")}
                required
                InputLabelProps={{
                  shrink: true
                }}
                error={!!errors['name']}
                disabled={!!state}
                helperText={errors['name']?.message || ""}
                {...register("name")}
              />
            </Grid>
            <Grid item={true} xs={4}>
              <NGTextField
                fullWidth={true}
                placeholder={t("common.Timesheet Description")}
                label={t("common.Timesheet Description")}
                InputLabelProps={{
                  shrink: true
                }}
                disabled={!!state}
                margin={"normal"}
                error={!!errors['description']}
                helperText={errors['description']?.message || ""}
                {...register("description")}
              />
            </Grid>
            <Grid item xs={3}>
              <NGInputLabel label={t("common.Timesheet Color")}/>
              <Select
                value={selectedColor || '#1E88E5'}
                onChange={(e) => {
                  setValue('timesheet_color', e.target.value);
                  trigger('timesheet_color');
                }}
                disabled={!!state}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 200, // 限制下拉菜单最大高度
                      overflowY: 'auto', // 超出高度时显示滚动条
                      '&::-webkit-scrollbar': {
                        width: 6,
                      },
                      '&::-webkit-scrollbar-track': {
                        bgColor: '#f1f1f1',
                        borderRadius: 3,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        bgColor: '#c1c1c1',
                        borderRadius: 3,
                        '&:hover': {
                          bgColor: '#a8a8a8',
                        }
                      }
                    },
                  },
                  sx: {
                    '& .MuiMenuItem-root': {
                      padding: '8px 12px',
                    }
                  }
                }}
                sx={{
                  height: '40px',
                  minWidth: '90%',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    height: '100%',
                  },
                  '&:hover': {
                    borderColor: '#b0b0b0',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#e0e0e0',
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#757575',
                    marginLeft: '8px',
                  }
                }}
                renderValue={(selected) => (
                  <Box sx={{
                    width: '100%',
                    height: '20px',
                    borderRadius: 0.5,
                    backgroundColor: selected || '#1E88E5',
                    border: selected ? 'none' : '1px dashed #bdbdbd',
                  }}/>
                )}
              >
                {presetColors.map(color => (
                  <MenuItem
                    key={color}
                    value={color}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e8f0fe',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: 20,
                        borderRadius: 0.5,
                        backgroundColor: color,
                        border: '1px solid #e0e0e0',
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        <Grid sx={{marginLeft: "20px"}} item xs={10}>
          <NGInputLabel label={t("common.Schedule Rule")}/>
        </Grid>
        <Grid item xs={10}>
          <Box sx={{
            borderRadius: '5px',
            background: '#fff',
            margin: "5px",
            marginTop: "0px",
            paddingBottom: "10px"
          }}>
            <Grid sx={{padding: "1px"}} container={true} component={"form"}>
              <TimesheetSetting ref={sheetRef} cycle={periodCycle} period={"week"} timesheet={timesheet}
                                view_data={state?.id} setOption={setOption} schedule_used={0}/>
            </Grid>

          </Box>
        </Grid>
        <Grid sx={{marginLeft: "20px"}} item xs={10}>
          <NGInputLabel label={t("common.ATT records statistics rule")}/>
        </Grid>

        <Grid item xs={10}>
          <Box sx={{
            borderRadius: '5px',
            background: '#fff',
            margin: "20px",
            marginTop: "0px",
          }}>
            <Grid sx={{padding: "16px"}} container={true} component={"form"}>

              <PatternSetting ref={patternRef} pattern={pattern} view_data={state?.id} option={option} schedule_used={0}/>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={10}>
          <Box sx={{
            borderRadius: '5px',
            background: '#fff',
            margin: "20px",
            marginTop: "0px",
          }}>
            <Grid sx={{padding: "16px"}} container={true} component={"form"}>
              <Grid container={true} component={"form"} item xs={10}>
                <Grid container={true} xs={5}>
                  <NGInputLabel label={t("att.timeSheet.Break Time")}/>
                  <Switch
                    sx={{marginTop: "-6px"}}
                    checked={break_loading}
                    disabled={!!state}
                    onChange={() => setBreakLoading(!break_loading)}
                    name="Break Time"
                    color="primary"
                  />
                </Grid>
                <Grid container={true} xs={5}>
                  <NGInputLabel label={t("Overtime")}/>
                  <Switch
                    sx={{marginTop: "-6px"}}
                    checked={overtime_loading}
                    disabled={!!state}
                    onChange={() => setOvertimeLoading(!overtime_loading)}
                    name="overtime"
                    color="primary"
                  />
                </Grid>
              </Grid>
              <Grid columnSpacing={10}>
                {break_loading ? <BreakTimeSetting ref={breakTimeRef} break_time={breakTime} view_data={state?.id} schedule_used={0}/> :
                  <Grid></Grid>}
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={10} sx={{
          marginBottom: "10px", marginLeft: "20px",
          display: 'flex', justifyContent: 'start'
        }}>
          <Box sx={{display: "inline-flex"}}>
            <Link
              underline={"none"}
              onClick={() => {
                Dialog.confirm({
                  width: "90vw",
                  title: t("organ.know more about this?"),
                  content: <KnowAboutThis/>,
                });
              }}
              sx={{
                cursor: "pointer",
                padding: "0 8px",
              }}
            >
              <Typography>{t("organ.know more about this?")}</Typography>
            </Link>
          </Box>
        </Grid>
        <Grid item xs={10} sx={{
          marginBottom: "10px", marginLeft: "20px",
          display: 'flex', justifyContent: 'flex-end'
        }}>
          {!state &&
            <Stack spacing={3} direction={"row"} justifyContent="flex-end">
              <CancelButton onClick={() => {
                props.info(false)
              }}/>
              <SaveButton type={'submit'} disabled={!!state} onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  const data = getValues();
                  await onSubmit(data);
                  props.info(false)
                  props.refresh(true)
                } else {
                  scrollTo("name")
                }
              }}/>
            </Stack>
          }
        </Grid>
      </Grid>
    </Box>
  )
}
export default NewTimesheetQuick
